/**
 * Drawer Service (drawerService.js)
 *
 * Provides a reactive service for managing the HTML content and lifecycle events of the drawer UI component.
 * Uses an RxJS Subject to emit and observe drawer-related actions across the application.
 *
 * Features:
 * - clearHtml: Emits an action to clear the drawer's HTML content.
 * - getHtml: Returns an observable stream of all drawer actions for subscription.
 * - onHtmlReady: Emits an action when the drawer's HTML is ready and inserted into the DOM.
 * - onHtmlRemoved: Emits an action when the drawer's HTML is removed from the DOM.
 * - setHtml: Emits an action to set or update the drawer's HTML content.
 *
 * This service enables decoupled communication between the drawer component and other parts of the application,
 * supporting a reactive, event-driven UI architecture.
 */

import { Subject } from 'rxjs';

const subject = new Subject();

export default {
  /**
   * Clears the HTML content of the drawer.
   */
  clearHtml: () => subject.next({ action: 'clearHtml' }),

  /**
   * Returns an observable stream of all drawer actions.
   */
  getHtml: () => subject.asObservable(),

  /**
   * Registers a callback to be invoked when the drawer's HTML is ready.
   */
  onHtmlReady: (data) => subject.next({ action: 'onHtmlReady', ...data }),

  /**
   * Emits an action when the drawer's HTML is removed from the DOM.
   */
  onHtmlRemoved: (data) => subject.next({ action: 'onHtmlRemoved', ...data }),

  /**
   * Sets the HTML content of the drawer.
   */
  setHtml: (data) => subject.next({ action: 'setHtml', value: data.payload }),

  /**
   * Mounts a live DOM element into the drawer, preserving event listeners.
   * @param {HTMLElement} element
   * @param {{ peek?: string }} [options] - Optional strategy options.
  *   `peek`: height string (e.g. `'72px'`). When set, the drawer starts expanded;
  *   swipe-down collapses it to that height instead of closing, and tap expands
  *   it back to `--open`. (Strategy Pattern)
   */
  mountElement: (element, options = {}) =>
    subject.next({ action: 'mountElement', value: element, options }),

  /**
   * Expands the drawer to full size (edge-to-edge, no drag-to-dismiss).
   * Sets an overlay flag that disables pointer dragging and applies the
   * `--full` state — the active gesture strategy (e.g. peek) is left
   * untouched underneath, not swapped.
   */
  expandToFullSize: () => subject.next({ action: 'expandToFullSize' }),

  /**
   * Collapses the drawer back from full size by clearing the overlay flag
   * set by expandToFullSize(), re-enabling pointer dragging under whichever
   * gesture strategy was already active the whole time.
   */
  collapseToPartial: () => subject.next({ action: 'collapseToPartial' }),

  /**
   * Expands the drawer out of its collapsed state programmatically, exactly as a user tap would.
   * No-op while full size, and under gesture strategies with no collapsed state (see `peek`).
   */
  expandPanel: () => subject.next({ action: 'expandPanel' }),

  /**
   * Collapses the drawer back to its peek height programmatically, exactly as a user swipe-down
   * would. No-op while full size, and under gesture strategies with no collapsed state.
   */
  collapsePanel: () => subject.next({ action: 'collapsePanel' }),

  /**
   * Hides the drawer container from view.
   */
  hideDrawer: () => subject.next({ action: 'hideDrawer' }),

  /**
   * Restores the drawer container visibility.
   */
  showDrawer: () => subject.next({ action: 'showDrawer' }),
};
